import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { fetchData } from '../../helpers/apiUtils';

/**
 * DOI-Danke-Seite, Webinar-Karte v2 (A/B-Test „Einladung“ / „Ticket“) — Faktenblatt je Webinar.
 *
 * Die Karte entsteht per Code aus dem Faktenblatt; jedes Fakt traegt ein woertliches Zitat aus einer
 * Heat-Up-Mail, der Pruefer (Server) laesst nur Belegtes durch. Plan: .claude/plans/2026-10-12-doi-seite-redesign-ab-test.md.
 * Ohne Heat-Ups („Vorab-Karte", 22.10.): Quelle ist die publizierte Anmeldeseite plus aktive Redlinks
 * (`source_kind: 'vorab'`); sobald Heat-Ups da sind, erneuert der Stundenlauf die Karte aus Heat-Ups UND
 * Anmeldeseite/Redlinks (`vorab_count` > 0, seit 23.10.).
 *
 * Seit Plan 2026-10-22 (A4) in zwei Teilen, die sich EINEN Zustand teilen (`useDoiKarte`, einmal im Raum-Tab):
 * - `DoiKarteStatus` (alle): zwei Vorschauen, eine Statuszeile in Worten, „Neu erzeugen“, Schalter
 *   „Bisherige Karte verwenden“.
 * - `DoiKarteEditor` (nur Admin, im Technik-Bereich): Faktenblatt von Hand + Bau-Hinweise. Der Server sperrt
 *   den PUT zusaetzlich fuer Nicht-Admins (`status = 'edited'` sperrt den Stundenlauf dauerhaft aus).
 */

type Arm = 'A' | 'B';

interface CardBullet {
  text: string;
  extra: string;
}

interface CardV2 {
  arm: Arm;
  headline: string;
  asset_prefix: string | null;
  lead: string | null;
  bullets: CardBullet[];
  cta: string;
  proof: string | null;
  eyebrow: string | null;
  live_label: string | null;
  live_label_desktop_extra: string | null;
  host: { name: string; role: string | null; photo_url: string | null } | null;
  date: { label: string; weekday: string; day: string; month: string; time: string; badge: string };
}

interface ArmPreview {
  card: CardV2 | null;
  notes: string[];
  lead_with_asset: string | null;
}

type FactsJson = Record<string, Record<string, unknown> | null | number | undefined>;

interface DoiCardView {
  row: {
    status: 'auto' | 'edited';
    updated_at: string;
    updated_by: string | null;
    model: string | null;
    rules_version: string;
    /** Schalter „Bisherige Karte verwenden“ (fehlt vor Migration 20261022 = aus). */
    v2_off?: boolean;
  } | null;
  facts: FactsJson | null;
  heatup_count: number;
  /** Woraus der nächste Lauf erzeugt: Heat-Ups, sonst vorab Anmeldeseite + Redlinks; null = noch nichts da. */
  source_kind?: 'heatup' | 'vorab' | null;
  /** Texte aus Anmeldeseite + Redlinks in der Quelle (bei `heatup` seit 23.10. dazugenommen; fehlt = älterer Server). */
  vorab_count?: number;
  /** Darunter die Anmeldeseite (false = nur Newsletter-Texte). */
  vorab_lp?: boolean;
  /** Anmeldeseite hat gespeicherte, nicht veröffentlichte Änderungen — bis zum Veröffentlichen ruht die Karte. */
  source_frozen?: boolean;
  stale: boolean;
  cron_enabled: boolean;
  /** Admin: Faktenblatt von Hand bearbeiten (Server entscheidet, Anzeige folgt). */
  can_edit?: boolean;
  problems: Array<{ key: string; reason: string }>;
  preview_asset: string;
  runtime: { start_ms: number; registrants: number | null; photo_url: string | null };
  preview: { A: ArmPreview; B: ArmPreview };
  extraction?: { model: string; kept: string[]; dropped: Array<{ key: string; reason: string }> };
}

/**
 * Feld-Editoren je Fakt (Vertrag Abschnitt 4). `num` = Zahl. Ein Punkt im Namen adressiert ein Unterobjekt
 * (`instrument.value` → `best_trade.instrument.value`); ist das Unterobjekt ganz leer, wird es `null`.
 */
const FACT_FIELDS: Array<{ key: string; label: string; fields: Array<{ name: string; label: string; num?: boolean }> }> = [
  { key: 'host', label: 'Referent', fields: [{ name: 'name', label: 'Name' }, { name: 'role', label: 'Rolle' }] },
  { key: 'asset_class', label: 'Anlageklasse', fields: [{ name: 'value', label: 'Wert' }] },
  { key: 'method_moment', label: 'Zeitpunkt / Methode', fields: [{ name: 'value', label: 'Wert' }] },
  { key: 'event_noun', label: 'Ereignis', fields: [{ name: 'value', label: 'Wert' }] },
  { key: 'criterion', label: 'Auswahlkriterium', fields: [{ name: 'value', label: 'Wert' }] },
  { key: 'universe', label: 'Geprüfte Firmen', fields: [{ name: 'value', label: 'Anzahl', num: true }] },
  { key: 'trades_per_year', label: 'Trades pro Jahr', fields: [{ name: 'value', label: 'Anzahl', num: true }] },
  {
    key: 'best_trade',
    label: 'Bester abgeschlossener Trade',
    fields: [
      { name: 'name', label: 'Wert/Aktie' },
      { name: 'instrument.value', label: 'Instrument' },
      { name: 'instrument.quote', label: 'Instrument-Zitat (dieselbe Mail)' },
      { name: 'from_eur', label: 'von €', num: true },
      { name: 'to_eur', label: 'auf €', num: true },
      { name: 'days', label: 'Tage', num: true },
    ],
  },
  { key: 'loss_shown', label: 'Verlust wird gezeigt', fields: [] },
  { key: 'bottom_line', label: 'Unter dem Strich', fields: [] },
  { key: 'next_target', label: 'Nächstes Ziel angekündigt', fields: [] },
  { key: 'market_mechanism', label: 'Marktmechanismus (≤ 80 Zeichen, ohne Zahlen)', fields: [{ name: 'value', label: 'Satz' }] },
  { key: 'duration_minutes', label: 'Dauer (Minuten)', fields: [{ name: 'value', label: 'Minuten', num: true }] },
  { key: 'signup_minutes', label: 'Anmeldedauer (Minuten)', fields: [{ name: 'value', label: 'Minuten', num: true }] },
  // Themen-Webinar (Karte „Welche Aktien … beim Thema … vorstellt"), greift, wenn die Methoden-Karte nicht baubar ist.
  { key: 'theme', label: 'Thema (ohne Jahreszahl, ≤ 30 Zeichen)', fields: [{ name: 'value', label: 'Wert' }] },
  { key: 'picks_count', label: 'Anzahl vorgestellter Aktien', fields: [{ name: 'value', label: 'Anzahl', num: true }] },
  { key: 'pick_1', label: 'Aktie 1 — Kurzbeschreibung (≤ 40 Zeichen)', fields: [{ name: 'value', label: 'Wert' }] },
  { key: 'pick_2', label: 'Aktie 2 — Kurzbeschreibung (≤ 40 Zeichen)', fields: [{ name: 'value', label: 'Wert' }] },
  { key: 'report_reveal', label: 'Namen + WKN im Report', fields: [] },
];

type FormFacts = Record<string, Record<string, string>>;

/** Zahl fuer das Eingabefeld in deutscher Schreibweise: 1423.17 → „1423,17" (ohne Tausenderpunkt). */
function formatFactNumber(v: unknown): string {
  if (typeof v !== 'number' || !Number.isFinite(v)) return v != null ? String(v) : '';
  return String(v).replace('.', ',');
}

/**
 * Eingabe → Zahl, Komma UND Punkt als Dezimalzeichen: „1423,17" / „1423.17" → 1423.17, „1.423,17" → 1423.17,
 * „1.423" → 1423. Ein Punkt gilt nur als Tausenderpunkt, wenn GENAU drei Ziffern folgen (und bei Komma-Dezimal
 * jeder Punkt so steht); sonst ist er das Dezimalzeichen. Unlesbar → null.
 */
function parseFactNumber(input: string): number | null {
  const s = input.trim().replace(/\s+/g, '');
  if (!s) return null;
  let norm: string;
  if (s.includes(',')) {
    // Komma = Dezimal; Punkte davor nur als Tausenderpunkte erlaubt.
    if (!/^-?\d{1,3}(\.\d{3})*(,\d+)?$/.test(s) && !/^-?\d+(,\d+)?$/.test(s)) return null;
    norm = s.replace(/\./g, '').replace(',', '.');
  } else if (/^-?\d{1,3}(\.\d{3})+$/.test(s)) {
    norm = s.replace(/\./g, '');
  } else if (/^-?\d+(\.\d+)?$/.test(s)) {
    norm = s;
  } else {
    return null;
  }
  const n = Number(norm);
  return Number.isFinite(n) ? n : null;
}

function readPath(src: Record<string, unknown> | null, name: string): unknown {
  const [a, b] = name.split('.');
  const v = src?.[a];
  if (b === undefined) return v;
  return v && typeof v === 'object' ? (v as Record<string, unknown>)[b] : undefined;
}

function formFromFacts(facts: FactsJson | null): FormFacts {
  const out: FormFacts = {};
  for (const f of FACT_FIELDS) {
    const src = (facts?.[f.key] || null) as Record<string, unknown> | null;
    const entry: Record<string, string> = { quote: src?.quote != null ? String(src.quote) : '' };
    for (const fld of f.fields) {
      const v = readPath(src, fld.name);
      entry[fld.name] = fld.num ? formatFactNumber(v) : v != null && typeof v !== 'object' ? String(v) : '';
    }
    out[f.key] = entry;
  }
  return out;
}

/** Leeres Zitat = Fakt entfaellt. Zahlenfelder als Zahl, leere Textfelder als null, leere Unterobjekte als null. */
function factsFromForm(form: FormFacts): FactsJson {
  const out: FactsJson = { version: 1 };
  for (const f of FACT_FIELDS) {
    const e = form[f.key] || {};
    const quote = (e.quote || '').trim();
    if (!quote) {
      out[f.key] = null;
      continue;
    }
    const fact: Record<string, unknown> = { quote };
    for (const fld of f.fields) {
      const v = (e[fld.name] || '').trim();
      const wert = fld.num ? parseFactNumber(v) : v || null;
      const [a, b] = fld.name.split('.');
      if (b === undefined) {
        fact[a] = wert;
      } else {
        const sub = (fact[a] as Record<string, unknown> | null | undefined) || {};
        sub[b] = wert;
        fact[a] = sub;
      }
    }
    for (const [k, v] of Object.entries(fact)) {
      if (v && typeof v === 'object' && Object.values(v as Record<string, unknown>).every((x) => x == null)) fact[k] = null;
    }
    out[f.key] = fact;
  }
  return out;
}

/** `[[x]]` → fett, sonst reiner Text (kein HTML aus dem Server). */
function Marked({ text }: { text: string | null | undefined }) {
  if (!text) return null;
  const parts = text.split(/(\[\[[^\]]*\]\])/g);
  return (
    <>
      {parts.map((p, i) =>
        p.startsWith('[[') && p.endsWith(']]') ? <b key={i}>{p.slice(2, -2)}</b> : <React.Fragment key={i}>{p}</React.Fragment>,
      )}
    </>
  );
}


const VARIANTE: Record<Arm, string> = { A: 'Variante A — Einladung', B: 'Variante B — Ticket' };

/** Vorschau einer Variante. `hinweise` (Bau-Hinweise des Servers, technisch) nur für den Admin. */
function ArmBox({ arm, p, hinweise }: { arm: Arm; p: ArmPreview; hinweise: boolean }) {
  const c = p.card;
  return (
    <Box sx={{ flex: 1, minWidth: 260, border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5, bgcolor: '#ffffff' }}>
      <Typography variant="subtitle2" gutterBottom>
        {VARIANTE[arm]}
      </Typography>
      {!c ? (
        <Typography variant="body2" color="text.secondary">
          Keine neue Karte für diese Variante — die DOI-Seite zeigt hier die bisherige Karte.
          {hinweise && p.notes.length > 0 && ` Bau-Hinweise: ${p.notes.join(' · ')}`}
        </Typography>
      ) : (
        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            {c.eyebrow || `${c.live_label ?? ''}${c.live_label_desktop_extra ?? ''}`} ·{' '}
            {arm === 'A' ? c.date.label : `${c.date.badge} ${c.date.weekday}, ${c.date.day} ${c.date.month} · ${c.date.time}`}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 700 }}>
            <Marked text={c.headline} />
          </Typography>
          {p.lead_with_asset && (
            <Typography variant="body2">
              <Marked text={p.lead_with_asset} />
            </Typography>
          )}
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {c.bullets.map((b, i) => (
              <li key={i}>
                <Typography variant="body2">
                  <Marked text={b.text} />
                  {b.extra && <span style={{ color: '#64748b' }}> · {b.extra}</span>}
                </Typography>
              </li>
            ))}
          </Box>
          {c.host && (
            <Typography variant="caption" color="text.secondary">
              {c.host.name}
              {c.host.role ? `, ${c.host.role}` : ''} {c.host.photo_url ? '· mit Foto' : '· ohne Foto'}
            </Typography>
          )}
          <Typography variant="body2">
            <b>[{c.cta}]</b> {c.proof && <Marked text={c.proof} />}
          </Typography>
          {hinweise && p.notes.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              Bau-Hinweise: {p.notes.join(' · ')}
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
}

/** Bau-Hinweise des Servers → was in den Heat-Up-Mails fehlt, in Worten (ohne Schlüssel). */
const FEHLT_WORT: Array<[RegExp, string]> = [
  [/host/, 'Referent'],
  [/asset_class/, 'Anlageklasse'],
  [/method_moment/, 'Methode bzw. Zeitpunkt'],
  [/next_target/, 'angekündigtes Ziel'],
  [/theme/, 'Thema'],
  [/picks_count/, 'Anzahl der vorgestellten Aktien'],
  [/kein Punkt/, 'belegbare Nutzen-Punkte'],
  [/zu lang/, 'kürzere Formulierungen (eine Zeile wäre zu lang)'],
  [/kein Faktenblatt/, 'verwertbare Angaben'],
];

export function doiFehltInWorten(notes: string[]): string {
  const out: string[] = [];
  for (const [re, wort] of FEHLT_WORT) if (notes.some((n) => re.test(n)) && !out.includes(wort)) out.push(wort);
  return out.join(', ');
}

export type DoiStatus = { art: 'ok' | 'info' | 'warnung'; text: string };

/** Woraus der nächste Lauf erzeugt. Ein älterer Server schickt `source_kind` nicht: dann zählen nur Heat-Ups. */
function doiQuelle(view: DoiCardView): 'heatup' | 'vorab' | null {
  return view.source_kind !== undefined ? view.source_kind : view.heatup_count > 0 ? 'heatup' : null;
}

/** Was zu den Heat-Ups dazukam, als Wort („Anmeldeseite“ / „Newsletter-Texte“); null = nichts. */
function doiZusatz(view: DoiCardView | null): 'Anmeldeseite' | 'Newsletter-Texte' | null {
  if (!view || doiQuelle(view) === 'vorab' || (view.vorab_count ?? 0) === 0) return null;
  return view.vorab_lp === false ? 'Newsletter-Texte' : 'Anmeldeseite';
}

/** Quelle im Dativ für Meldungen („aus …“, „in …“). Heat-Ups kommen seit 23.10. mit Anmeldeseite/Redlinks zusammen. */
function doiQuelleText(view: DoiCardView | null): string {
  if (view && doiQuelle(view) === 'vorab') return 'der Anmeldeseite';
  const zusatz = doiZusatz(view);
  if (!zusatz) return 'den Heat-Up-Mails';
  return zusatz === 'Anmeldeseite' ? 'den Heat-Up-Mails und der Anmeldeseite' : 'den Heat-Up-Mails und den Newsletter-Texten';
}

/** Eine Statuszeile für Marketing — „nichts zu tun“, wo nichts zu tun ist; sonst was fehlt in Worten. */
export function doiStatus(view: DoiCardView): DoiStatus {
  if (view.row?.v2_off) {
    return {
      art: 'info',
      text: 'Läuft mit der bisherigen Karte — so eingestellt (Schalter unten). Nichts zu tun.',
    };
  }
  const kind = doiQuelle(view);
  // Vorab = noch keine Heat-Up-Mails; die Karte kommt dann aus der Anmeldeseite (und Newsletter-Texten).
  const vorab = kind === 'vorab';
  // Heat-Ups zusammen mit Anmeldeseite/Newsletter-Texten (seit 23.10. der Normalfall, sobald beides da ist).
  const zusatz = doiZusatz(view);
  const quelle = doiQuelleText(view);
  // Anmeldeseite mit gespeicherten, noch nicht veröffentlichten Änderungen: bis zum Veröffentlichen ruht alles.
  const eingefroren = !!view.source_frozen;
  if (!view.row) {
    if (!kind) {
      return {
        art: 'info',
        text: 'Läuft mit der bisherigen Karte — nichts zu tun. Die neue Karte entsteht automatisch, sobald die Anmeldeseite veröffentlicht ist oder Heat-Up-Mails geplant sind.',
      };
    }
    if (eingefroren) {
      return {
        art: 'info',
        text: view.cron_enabled
          ? 'Läuft mit der bisherigen Karte — nichts zu tun. Die Anmeldeseite hat noch nicht veröffentlichte Änderungen; die neue Karte entsteht nach dem Veröffentlichen.'
          : 'Läuft mit der bisherigen Karte. Die Anmeldeseite hat noch nicht veröffentlichte Änderungen; nach dem Veröffentlichen entsteht die neue Karte mit „Neu erzeugen“ (die Automatik ist gerade aus).',
      };
    }
    return view.cron_enabled
      ? {
          art: 'info',
          text: `Läuft mit der bisherigen Karte — nichts zu tun. Die neue Karte wird beim nächsten stündlichen Lauf aus ${quelle} erstellt.`,
        }
      : {
          art: 'info',
          text: 'Läuft mit der bisherigen Karte. Die Automatik ist gerade aus — mit „Neu erzeugen“ entsteht die neue Karte sofort.',
        };
  }
  const vonHand = view.row.status === 'edited';
  // Nur dann passiert etwas von selbst: der Stundenlauf erneuert ausschließlich automatisch erstellte Blätter.
  const erneuertSelbst = !vonHand && view.cron_enabled;
  const a = !!view.preview.A.card;
  const b = !!view.preview.B.card;
  if (a && b) {
    if (eingefroren) {
      return {
        art: 'ok',
        text: `${vonHand ? 'Läuft — vom Admin von Hand angepasst.' : vorab ? 'Vorab-Karte aus der Anmeldeseite.' : 'Läuft.'} Nichts zu tun — die Anmeldeseite hat noch nicht veröffentlichte Änderungen${erneuertSelbst ? '; nach dem Veröffentlichen wird die Karte automatisch angepasst' : ''}.`,
      };
    }
    // Hat sich die Quelle geändert, stammt die laufende Karte noch aus der ALTEN — keine Herkunft behaupten.
    const herkunft = vonHand
      ? 'Läuft — vom Admin von Hand angepasst.'
      : view.stale
        ? 'Läuft.'
        : vorab
          ? 'Vorab-Karte aus der Anmeldeseite.'
          : `Automatisch aus ${quelle} erstellt.`;
    // Quelle neu/geändert und die Automatik erneuert von selbst: kein Handlungsbedarf — auch wenn die alte
    // Karte gegen die neue Quelle nicht mehr belegt ist (so beim Wechsel von der Vorab-Karte auf die Heat-Ups).
    if (view.stale && erneuertSelbst) {
      return {
        art: 'ok',
        text: `${herkunft} Nichts zu tun — ${vorab ? 'die Anmeldeseite hat sich geändert' : zusatz ? `Heat-Up-Mails oder ${zusatz} sind neu oder wurden geändert` : 'die Heat-Up-Mails sind neu oder wurden geändert'}; die Karte wird beim nächsten stündlichen Lauf daraus erneuert.`,
      };
    }
    if (view.problems.length > 0) {
      return {
        art: 'warnung',
        text: `${herkunft} ${vorab ? 'Die Anmeldeseite wurde' : zusatz ? `Die Heat-Up-Mails oder die ${zusatz} wurden` : 'Die Heat-Up-Mails wurden'} seitdem geändert, ein Teil der Karte ist dort nicht mehr belegt. Bitte „Neu erzeugen“ klicken.`,
      };
    }
    return {
      art: 'ok',
      text:
        vorab && erneuertSelbst
          ? `${herkunft} Nichts zu tun — sobald Heat-Up-Mails geplant sind, wird sie automatisch daraus erneuert.`
          : `${herkunft} Nichts zu tun.`,
    };
  }
  const fehlt = doiFehltInWorten([...view.preview.A.notes, ...view.preview.B.notes]);
  const welche = !a && !b ? 'beide Varianten' : !a ? 'Variante A' : 'Variante B';
  // Herkunft nur nennen, solange die Quelle dieselbe ist, aus der das Blatt stammt (sonst: „noch nicht").
  const woraus = eingefroren || view.stale ? '' : ` aus ${quelle}`;
  const anfang = `Die ${vorab && !view.stale ? 'Vorab-' : 'neue '}Karte ließ sich${woraus} noch nicht vollständig bauen${fehlt ? ` — es fehlt: ${fehlt}` : ''}. Für ${welche} läuft so lange die bisherige Karte.`;
  if (eingefroren) {
    return {
      art: 'warnung',
      text: `${anfang} Die Anmeldeseite hat noch nicht veröffentlichte Änderungen — ${erneuertSelbst ? 'nach dem Veröffentlichen versucht es die Automatik von selbst erneut.' : 'nach dem Veröffentlichen bitte „Neu erzeugen“ klicken.'}`,
    };
  }
  if (view.stale && erneuertSelbst) {
    return {
      art: 'info',
      text: `${anfang} ${vorab ? 'Die Anmeldeseite hat sich geändert' : zusatz ? `Heat-Up-Mails oder ${zusatz} sind neu oder wurden geändert` : 'Heat-Up-Mails sind neu oder wurden geändert'}; der nächste stündliche Lauf erzeugt die Karte daraus neu — nichts zu tun.`,
    };
  }
  const weiter = !erneuertSelbst
    ? vonHand
      ? ' Das Faktenblatt wurde von Hand angepasst und wird nicht automatisch erneuert — „Neu erzeugen“ baut es neu.'
      : ' Die Automatik ist gerade aus — „Neu erzeugen“ versucht es sofort.'
    : vorab
      ? ' Sobald Heat-Up-Mails geplant sind, versucht es die Automatik von selbst erneut — nichts zu tun.'
      : ' Der nächste automatische Versuch kommt von selbst; schneller geht es, wenn die Heat-Up-Mails die Angaben enthalten und dann „Neu erzeugen“ geklickt wird.';
  return { art: 'warnung', text: anfang + weiter };
}

type Meldung = { text: string; severity: 'success' | 'error' | 'info' | 'warning' } | null;

/** Gemeinsamer Zustand der DOI-Karte (einmal je Raum-Tab aufrufen, an Status UND Editor geben). */
export function useDoiKarte(webinarId: string | null | undefined) {
  const [view, setView] = useState<DoiCardView | null>(null);
  const [form, setForm] = useState<FormFacts>(formFromFacts(null));
  const [busy, setBusy] = useState<'load' | 'save' | 'regen' | 'schalter' | null>(null);
  const [message, setMessage] = useState<Meldung>(null);
  const loadKeyRef = useRef<string | null>(null);
  const base = webinarId ? `/api/webinar-room-admin/${encodeURIComponent(webinarId)}/doi-card` : null;

  const apply = useCallback((d: DoiCardView) => {
    setView(d);
    setForm(formFromFacts(d.facts));
  }, []);

  const load = useCallback(async () => {
    if (!base || !webinarId) return;
    const key = webinarId;
    loadKeyRef.current = key;
    setBusy('load');
    const r = await fetchData<DoiCardView>(base);
    if (loadKeyRef.current !== key) return;
    setBusy(null);
    if (!r.success || !r.data) {
      setMessage({ text: r.error || 'DOI-Karte konnte nicht geladen werden', severity: 'error' });
      return;
    }
    apply(r.data);
  }, [base, webinarId, apply]);

  useEffect(() => {
    // Datensatzwechsel: erst leeren, dann holen (kein Stand des vorigen Webinars unter neuem Titel).
    setView(null);
    setForm(formFromFacts(null));
    setMessage(null);
    setBusy(null);
    void load();
  }, [load]);

  /** Antwort nur übernehmen, wenn noch dasselbe Webinar gewählt ist. */
  const fuerMich = (key: string | null | undefined) => key != null && loadKeyRef.current === key;

  const save = async () => {
    if (!base) return;
    const key = webinarId;
    setBusy('save');
    setMessage(null);
    const r = await fetchData<DoiCardView>(base, {
      method: 'PUT',
      body: JSON.stringify({ facts: factsFromForm(form), expected_updated_at: view?.row?.updated_at ?? null }),
    });
    if (!fuerMich(key)) return;
    setBusy(null);
    if (!r.success || !r.data) {
      setMessage({ text: r.error || 'Speichern fehlgeschlagen', severity: 'error' });
      return;
    }
    apply(r.data);
    setMessage({ text: 'Gespeichert — Status „von Hand“, der Stundenlauf überschreibt diese Fassung nicht.', severity: 'success' });
  };

  const regenerate = async (confirm = false): Promise<void> => {
    if (!base) return;
    const key = webinarId;
    setBusy('regen');
    setMessage({
      text: `Die Karte wird aus ${doiQuelleText(view)} neu erzeugt (dauert bis zu 3 Minuten) …`,
      severity: 'info',
    });
    const r = await fetchData<DoiCardView>(`${base}/regenerate`, {
      method: 'POST',
      body: JSON.stringify({ confirm, expected_updated_at: view?.row?.updated_at ?? null }),
    });
    if (!fuerMich(key)) return;
    setBusy(null);
    if (!r.success && r.requires_confirmation && !confirm) {
      if (window.confirm(`${r.error || 'Die Karte wurde von Hand angepasst.'} Trotzdem neu erzeugen?`)) {
        await regenerate(true);
      } else {
        setMessage(null);
      }
      return;
    }
    if (!r.success || !r.data) {
      setMessage({ text: r.error || 'Neu erzeugen fehlgeschlagen', severity: 'error' });
      return;
    }
    apply(r.data);
    const ex = r.data.extraction;
    const dropped = ex?.dropped.length ?? 0;
    setMessage({
      text: dropped
        ? `Neu erzeugt. ${dropped} Angabe(n) waren in ${doiQuelleText(r.data)} nicht belegt und wurden weggelassen.`
        : 'Neu erzeugt.',
      severity: dropped ? 'warning' : 'success',
    });
  };

  const setV2Off = async (off: boolean) => {
    if (!base) return;
    const key = webinarId;
    setBusy('schalter');
    setMessage(null);
    const r = await fetchData<DoiCardView>(`${base}/v2-off`, { method: 'PUT', body: JSON.stringify({ off }) });
    if (!fuerMich(key)) return;
    setBusy(null);
    if (!r.success || !r.data) {
      setMessage({ text: r.error || 'Umschalten fehlgeschlagen', severity: 'error' });
      return;
    }
    apply(r.data);
    setMessage({
      text: off
        ? 'Die DOI-Seiten zeigen für dieses Webinar wieder die bisherige Karte (wirkt binnen etwa einer Minute).'
        : 'Die DOI-Seiten zeigen wieder die neue Karte (wirkt binnen etwa einer Minute).',
      severity: 'success',
    });
  };

  const setField = (key: string, name: string, value: string) =>
    setForm((f) => ({ ...f, [key]: { ...(f[key] || {}), [name]: value } }));

  return { view, form, busy, message, load, save, regenerate, setV2Off, setField };
}

export type DoiKarte = ReturnType<typeof useDoiKarte>;

const STATUS_FARBE: Record<DoiStatus['art'], 'success' | 'info' | 'warning'> = {
  ok: 'success',
  info: 'info',
  warnung: 'warning',
};

/** Für alle: Vorschauen, Statuszeile, „Neu erzeugen“, Schalter „Bisherige Karte verwenden“. */
export const DoiKarteStatus: React.FC<{ k: DoiKarte; admin: boolean }> = ({ k, admin }) => {
  const { view, busy, message } = k;
  if (!view) {
    return busy === 'load' ? (
      <CircularProgress size={20} />
    ) : (
      <>{message && <Alert severity={message.severity}>{message.text}</Alert>}</>
    );
  }
  const status = doiStatus(view);
  const v2Off = !!view.row?.v2_off;
  return (
    <Stack spacing={1.5}>
      <Alert severity={STATUS_FARBE[status.art]}>{status.text}</Alert>
      {message && <Alert severity={message.severity}>{message.text}</Alert>}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
        <ArmBox arm="A" p={view.preview.A} hinweise={admin} />
        <ArmBox arm="B" p={view.preview.B} hinweise={admin} />
      </Stack>
      <Typography variant="caption" color="text.secondary">
        So erscheint die Karte auf den DOI-Danke-Seiten (Beispiel-Aktie „{view.preview_asset}“, Anmeldungen
        jetzt: {view.runtime.registrants ?? '—'}). Jede Besucherin sieht zufällig Variante A oder B.
        {v2Off && ' Solange „Bisherige Karte verwenden“ an ist, wird keine der beiden gezeigt.'}
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
        <Button variant="outlined" onClick={() => void k.regenerate(false)} disabled={busy !== null}>
          {busy === 'regen' ? 'Wird erzeugt …' : 'Neu erzeugen'}
        </Button>
        <FormControlLabel
          control={
            <Switch
              checked={v2Off}
              disabled={busy !== null || !view.row}
              onChange={(e) => void k.setV2Off(e.target.checked)}
            />
          }
          label="Bisherige Karte verwenden"
        />
      </Stack>
      <Typography variant="caption" color="text.secondary">
        „Neu erzeugen“ liest Heat-Up-Mails und Anmeldeseite jetzt sofort neu (sonst passiert das stündlich von
        selbst). „Bisherige
        Karte verwenden“ zeigt auf den DOI-Seiten wieder die frühere, einfache Karte — z. B. wenn die neue Karte etwas
        Falsches sagt.{!view.row && ' Der Schalter steht bereit, sobald die neue Karte einmal erstellt wurde.'}
      </Typography>
    </Stack>
  );
};

/** Nur Admin (im Technik-Bereich): Faktenblatt von Hand, Stand und Bau-Hinweise. Zugeklappt. */
export const DoiKarteEditor: React.FC<{ k: DoiKarte }> = ({ k }) => {
  const { view, form, busy, message } = k;
  return (
    <Accordion disableGutters elevation={0} sx={{ border: '1px solid #e5e7eb', '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box>
          <Typography variant="subtitle2">Faktenblatt der DOI-Karte bearbeiten</Typography>
          <Typography variant="caption" color="text.secondary">
            Speichern setzt den Status „von Hand“ — der Stundenlauf erneuert die Karte danach nie mehr (auch nicht bei
            geänderten Heat-Ups). Nur nutzen, wenn „Neu erzeugen“ nicht reicht.
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1.5}>
          {view && (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              {view.row ? (
                <Chip
                  size="small"
                  color={view.row.status === 'edited' ? 'warning' : 'success'}
                  label={view.row.status === 'edited' ? 'von Hand' : 'automatisch'}
                />
              ) : (
                <Chip size="small" label="noch kein Faktenblatt" />
              )}
              {view.row && (
                <Typography variant="caption" color="text.secondary">
                  Stand {view.row.updated_at.slice(0, 16)} UTC · {view.row.updated_by || '—'}
                  {view.row.model ? ` · ${view.row.model}` : ''} · Regeln {view.row.rules_version}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                {view.source_frozen
                  ? `Quelle: ${doiQuelle(view) === 'heatup' ? `${view.heatup_count} Heat-Up(s) + ` : ''}Anmeldeseite — ruht bis zum Veröffentlichen der gespeicherten Änderungen`
                  : doiQuelle(view) === 'vorab'
                  ? 'Quelle: Anmeldeseite + Redlinks (noch keine Heat-Ups)'
                  : doiQuelle(view) === 'heatup'
                    ? `Quelle: ${view.heatup_count} Heat-Up(s)${doiZusatz(view) === 'Anmeldeseite' ? ' + Anmeldeseite/Redlinks' : doiZusatz(view) ? ' + Newsletter-Texte' : ''}`
                    : 'Keine Quelle'}{' '}
                · Stundenlauf {view.cron_enabled ? 'an' : 'aus'}
              </Typography>
            </Stack>
          )}
          {view && view.problems.length > 0 && (
            <Alert severity="error">
              Gegen die aktuelle Quelle nicht mehr belegt: {view.problems.map((p) => `${p.key} (${p.reason})`).join('; ')}
            </Alert>
          )}
          {view?.can_edit === false && (
            <Alert severity="info">Der Server lässt das Speichern nur für den Admin zu.</Alert>
          )}
          {message && <Alert severity={message.severity}>{message.text}</Alert>}
          {FACT_FIELDS.map((f) => {
            const e = form[f.key] || {};
            return (
              <Box key={f.key} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {f.label}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 0.5 }}>
                  {f.fields.map((fld) => (
                    <TextField
                      key={fld.name}
                      size="small"
                      label={fld.label}
                      value={e[fld.name] || ''}
                      onChange={(ev) => k.setField(f.key, fld.name, ev.target.value)}
                      sx={{ minWidth: fld.num ? 90 : 160 }}
                    />
                  ))}
                </Stack>
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  label="Zitat (wörtlich aus einer Heat-Up-Mail, der Anmeldeseite oder einem Newsletter-Text; leer = Fakt entfällt)"
                  value={e.quote || ''}
                  onChange={(ev) => k.setField(f.key, 'quote', ev.target.value)}
                  sx={{ mt: 1 }}
                />
              </Box>
            );
          })}
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              onClick={() => void k.save()}
              disabled={busy !== null || !view || view.can_edit === false}
            >
              {busy === 'save' ? 'Speichert …' : 'Faktenblatt speichern'}
            </Button>
            <Button onClick={() => void k.load()} disabled={busy !== null}>
              Neu laden
            </Button>
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
